import {
  MockHook,
  JupiterTheme,
  LoadingSpinner,
} from '@jupiterone/jupiter-web-toolkit';
import {
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Typography,
  Tooltip,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import {
  Edit as EditIcon,
  Plus as AddIcon,
  Trash as DeleteIcon,
} from 'react-feather';
import useQueryPermissionsLogic, {
  QueryPermissionsComponentProps,
} from './useQueryPermissionsLogic';
import PermissionListItem from './PermissionListItem';
import EditPermissionDialog from './EditPermissionDialog';
import DeletePermissionDialog from './DeletePermissionDialog';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    tabContainer: {
      color: theme.palette.common.white,
      marginBottom: theme.spacing(5),
    },
    tabHeader: {
      color: theme.palette.primary.main,
    },
    tabHeaderSpacing: {
      marginBottom: theme.spacing(1),
    },
    tabSubHeaderContainer: {
      display: 'flex',
      marginBottom: theme.spacing(2),
    },
    listItemContainer: {
      borderBottom: `1px solid ${theme.palette.grey[100]}`,
      color: theme.palette.common.white,
      display: 'block',
      '&:first-child': {
        borderTop: `1px solid ${theme.palette.grey[100]}`,
      },
      paddingRight: theme.spacing(9),
    },
    listItemRoot: {
      display: 'block',
      padding: theme.spacing(2, 0),
    },
    listItemText: {
      color: 'inherit',
      fontSize: '.8rem',
    },
    bold: {
      fontWeight: 'bold',
    },
    listItemAction: {
      right: 0,
      top: theme.spacing(3),
    },
    button: {
      color: theme.palette.common.white,
    },
    deleteButton: {
      color: theme.palette.error.contrastText,
      backgroundColor: theme.palette.error.main,
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
      },
    },
    placeholder: {
      padding: theme.spacing(3, 2),
      textAlign: 'center',
    },
  })
);

export const QueryPermissionsComponent = ({
  permissionSets,
  loading,
  saving,
  editing,
  deleting,
  handlePermissionUpsert,
  handlePermissionDelete,
  handleAddNewPermissionSet,
  handleEditCancel,
  handleDeleteCancel,
}: QueryPermissionsComponentProps) => {
  const classes = useStyles();

  return (
    <div className={classes.tabContainer}>
      <div className={classes.tabSubHeaderContainer}>
        <div>
          <Typography
            className={`${classes.tabHeader} ${classes.tabHeaderSpacing}`}
            variant="subtitle1"
          >
            Query Permissions
          </Typography>
          <Typography variant="body1">
            Users in this group can only query entities allowed by ANY of the
            following permission sets.
          </Typography>
        </div>
        <div>
          <Tooltip title="Add query permission set" placement="left">
            <span>
              <Fab
                color="primary"
                aria-label="Add query permission set"
                onClick={handleAddNewPermissionSet}
                size="small"
                disabled={loading || saving}
              >
                <AddIcon />
              </Fab>
            </span>
          </Tooltip>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <List>
          {permissionSets.length ? (
            permissionSets.map(
              ({
                title,
                entries,
                handleEditPermissionSet,
                handleDeletePermissionSet,
              }) => (
                <ListItem
                  key={title}
                  classes={{
                    root: classes.listItemRoot,
                    container: classes.listItemContainer,
                  }}
                  disableGutters={true}
                >
                  <div>
                    <Typography>{title}</Typography>
                    {entries.map(([permissionSetKey, permissionSetValues]) => (
                      <PermissionListItem
                        key={permissionSetKey}
                        permissionSetKey={permissionSetKey}
                        permissionSetValues={permissionSetValues}
                      />
                    ))}
                  </div>
                  <ListItemSecondaryAction className={classes.listItemAction}>
                    <IconButton
                      aria-label={`Edit ${title}`}
                      size="small"
                      onClick={handleEditPermissionSet}
                    >
                      <EditIcon className={classes.button} aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      aria-label={`Delete ${title}`}
                      size="small"
                      onClick={handleDeletePermissionSet}
                    >
                      <DeleteIcon
                        className={classes.button}
                        aria-hidden="true"
                      />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              )
            )
          ) : (
            <ListItem
              className={`${classes.listItemRoot} ${classes.listItemContainer} ${classes.placeholder}`}
              disableGutters={true}
            >
              You have no query permission sets yet
            </ListItem>
          )}
        </List>
      )}

      {!!editing && (
        <EditPermissionDialog
          permissionSet={editing.permissionSet}
          title={editing.title}
          onSave={handlePermissionUpsert}
          onCancel={handleEditCancel}
          saving={saving}
        />
      )}

      {!!deleting && (
        <DeletePermissionDialog
          title={deleting.title}
          onDelete={handlePermissionDelete}
          onCancel={handleDeleteCancel}
          saving={saving}
        />
      )}
    </div>
  );
};

export default MockHook.withHookHoc(
  QueryPermissionsComponent,
  useQueryPermissionsLogic
);
